const HeaderConfig = {
  hiddenTranslate: '0 -100%',
  hideRatio: 0.95,
  scrollDelta: 5,
  topbarSelector: '[ref="topbar"]',
  visibleTranslate: '0 0'
}

class UiHeader extends HTMLElement {
  #abortController = null
  #frame = 0
  #isHidden = false
  #lastScrollY = 0
  #topbar = null
  #topbarHeight = 0
  #topbarShift = 0
  #transitionFrame = 0

  #onLayoutChange = () => {
    this.#topbar = this.querySelector(HeaderConfig.topbarSelector)
    this.#measureTopbar()
    this.#requestHeaderUpdate()
  }

  #onPageShow = () => {
    this.#lastScrollY = Math.max(0, window.scrollY)
    this.style.transition = 'none'
    this.#measureTopbar()
    this.#updateHeaderPosition()
    this.#scheduleTransitionRestore()
  }

  #onBeforeUnload = () => {
    this.style.transition = 'none'
  }

  #requestHeaderUpdate = () => {
    if (this.#frame) {
      return
    }

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0
      this.#updateHeaderPosition()
    })
  }

  connectedCallback() {
    this.#topbar = this.querySelector(HeaderConfig.topbarSelector)
    if (!(this.#hidesOnScroll || this.#topbar)) return
    this.#abortController = new AbortController()
    this.#lastScrollY = Math.max(0, window.scrollY)
    this.#listenForViewportChanges()
    this.#listenForEditorChanges()
    this.style.transition = 'none'
    this.#measureTopbar()
    this.#updateHeaderPosition()
    this.#scheduleTransitionRestore()
  }

  get #hidesOnScroll() {
    return this.dataset.hideOnScroll !== 'false'
  }

  get #hideThreshold() {
    return window.innerHeight * HeaderConfig.hideRatio
  }

  #measureTopbar() {
    const height = this.#topbar?.getBoundingClientRect().height ?? 0
    this.#topbarHeight = Number.isFinite(height) ? height : 0
  }

  #listenForViewportChanges() {
    const { signal } = this.#abortController
    window.addEventListener('scroll', this.#requestHeaderUpdate, {
      passive: true,
      signal
    })
    window.addEventListener('resize', this.#onLayoutChange, { signal })
    window.addEventListener('pageshow', this.#onPageShow, { signal })
    window.addEventListener('beforeunload', this.#onBeforeUnload, { signal })
  }

  #listenForEditorChanges() {
    const { signal } = this.#abortController
    document.addEventListener('shopify:section:load', this.#onLayoutChange, {
      signal
    })
    document.addEventListener('shopify:block:select', this.#onLayoutChange, {
      signal
    })
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    this.#cancelFrames()
    this.#isHidden = false
    this.#topbarShift = 0
    this.style.transition = 'none'
    this.style.removeProperty('translate')
    this.style.removeProperty('transform')
    this.#topbar?.removeAttribute('inert')
  }

  #cancelFrames() {
    cancelAnimationFrame(this.#frame)
    cancelAnimationFrame(this.#transitionFrame)
    this.#frame = 0
    this.#transitionFrame = 0
  }

  #scheduleTransitionRestore() {
    cancelAnimationFrame(this.#transitionFrame)
    this.#transitionFrame = requestAnimationFrame(() => {
      this.#transitionFrame = 0
      this.style.removeProperty('transition')
    })
  }

  #updateHeaderPosition() {
    const scrollY = Math.max(0, window.scrollY)
    this.#unpinTopbar(scrollY)

    if (!this.#hidesOnScroll) {
      return
    }

    this.#updateHeaderVisibility(scrollY)
  }

  /** @param {number} scrollY */
  #unpinTopbar(scrollY) {
    const shift = Math.min(scrollY, this.#topbarHeight)
    if (shift === this.#topbarShift) {
      return
    }

    this.#topbarShift = shift
    this.style.transform = `translate3d(0, ${-shift}px, 0)`
    this.#topbar?.toggleAttribute('inert', shift >= this.#topbarHeight)
  }

  /** @param {number} scrollY */
  #updateHeaderVisibility(scrollY) {
    const delta = scrollY - this.#lastScrollY

    if (Math.abs(delta) >= HeaderConfig.scrollDelta) {
      this.#lastScrollY = scrollY
      this.#setHeaderHiddenState(delta > 0 && scrollY > this.#hideThreshold)
      return
    }

    if (scrollY <= this.#hideThreshold) {
      this.#setHeaderHiddenState(false)
    }
  }

  /** @param {boolean} isHidden */
  #setHeaderHiddenState(isHidden) {
    if (isHidden === this.#isHidden) {
      return
    }

    this.#isHidden = isHidden
    this.style.translate = isHidden
      ? HeaderConfig.hiddenTranslate
      : HeaderConfig.visibleTranslate
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', UiHeader)
}
